/**
 * The chrome every signed-in page sits inside.
 *
 * Built to the two mockups: a rail down the left on a wide screen, a bar
 * across the bottom on a narrow one. Same destinations either way, because
 * they are the same app - what changes is where a thumb can reach.
 *
 * ## What wears it, and what does not
 *
 * Pages you move BETWEEN wear it: your games, favorites, recently played, the
 * store, your account. Pages you are somewhere specific wear their own
 * heading instead - a game's page, the join screens, the table. The landing
 * page is for people who are not in the app yet and has its own bar.
 *
 * The rule of thumb: if the answer to "where am I" is a place on this rail,
 * the rail should be showing it. If the answer is "in a game of Hearts", the
 * rail is furniture in the way.
 *
 * ## Store is here and is not in the mockup
 *
 * The mockup's rail is Games, Favorites, Recently Played, My Stats, Settings -
 * no store, because the picture was drawn of a catalogue rather than of a shop
 * that has one. Ours has to be reachable from somewhere, and the alternative
 * was leaving the pill on every page's own header, which is the header this
 * replaces.
 *
 * My Stats arrived with `recordPlayerStats`, which counts a finished game
 * against each player who sat at it. Before that the only record of a
 * finished game was keyed by the MATCH, with the seats inside, so there was
 * no way in from a player.
 */

import { NavLink, Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Icon } from '../render/Icon.js';
import type { IconName } from '../render/Icon.js';
import { useSession } from '../session-context.js';

interface Destination {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
}

/**
 * In the order the mockup draws them: what you play, then the two lists you
 * built by playing, then the shop, then you.
 */
const DESTINATIONS: readonly Destination[] = [
  { to: '/library', label: 'Games', icon: 'gamepad' },
  { to: '/favorites', label: 'Favorites', icon: 'heart' },
  { to: '/recent', label: 'Recently Played', icon: 'clock' },
  { to: '/stats', label: 'My Stats', icon: 'difficulty' },
  { to: '/store', label: 'Store', icon: 'dice' },
  { to: '/account', label: 'Settings', icon: 'info' },
];

/** What a destination is called when six of them share a phone's width. */
const SHORT: Record<string, string> = {
  'Recently Played': 'Recent',
  'My Stats': 'Stats',
};

export function AppFrame({ children }: { children: ReactNode }) {
  const { session } = useSession();

  return (
    <div className="app-frame">
      {/*
        The rail, on a screen wide enough to spare the width. Hidden rather
        than unmounted on a phone, so there is one nav in the document and a
        screen reader is not offered the same five places twice.
      */}
      <nav className="app-rail" aria-label="Sections">
        <Link className="app-rail-brand" to="/">
          <img src="/art/logo-small.webp" width={320} height={97} alt="Revelry" />
        </Link>

        <ul className="app-rail-list">
          {DESTINATIONS.map((place) => (
            <li key={place.to}>
              <NavLink
                to={place.to}
                className={({ isActive }) => (isActive ? 'app-rail-link current' : 'app-rail-link')}
              >
                <Icon name={place.icon} />
                <span>{place.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Bottom of the rail, where the mockup puts it. Signed out, the
            useful thing here is the way in rather than a name. */}
        <div className="app-rail-foot">
          {session ? (
            <Link className="app-rail-you" to="/account">
              <span className="app-rail-avatar" aria-hidden="true">
                <Icon name="people" />
              </span>
              <span>Your account</span>
            </Link>
          ) : (
            <Link className="pill-button" to="/login">
              Sign in
            </Link>
          )}
        </div>
      </nav>

      <div className="app-main">{children}</div>

      {/*
        The same places, across the bottom, where a thumb is.

        `aria-hidden` on the labels would be wrong - they are the only thing
        naming these on a phone - so this is a second nav in the document and
        the rail above is the one that hides. Which of the two is showing is a
        media query's business, and CSS is where that belongs.
      */}
      <nav className="app-tabs" aria-label="Sections">
        {DESTINATIONS.map((place) => (
          <NavLink
            key={place.to}
            to={place.to}
            className={({ isActive }) => (isActive ? 'app-tab current' : 'app-tab')}
          >
            <Icon name={place.icon} />
            {/* Shorter across the bottom, where six labels share a phone's
                width. The rail has room for the whole name. */}
            <span>{SHORT[place.label] ?? place.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
